import { AUTH_GRAVITEE_ENABLED } from '@/lib/constants';
import { clearSession } from '@/lib/storage';
import { useUiStore } from '@/stores/ui-store';
import { refreshGraviteeToken } from '@/lib/auth/gravitee';

/*
 * Garde de session commune aux clients v1 et v2.
 *
 * Un 401 dit que le Pass ne reconnaît plus le jeton. En MFA, on tente un rafraîchissement du jeton
 * Gravitee (un seul à la fois, les appels concurrents attendent son issue) avant de rejouer la
 * requête ; sinon, ou si le rafraîchissement échoue, la session est fermée.
 */

let isRefreshing = false;
let refreshQueue: ((success: boolean) => void)[] = [];

const processQueue = (success: boolean) => {
    refreshQueue.forEach((cb) => cb(success));
    refreshQueue = [];
};

/**
 * Éjection après perte de session.
 *
 * `gravitee_force_login` fait ajouter `prompt=login` au prochain /oauth/authorize.
 * Sans lui, la session SSO Gravitee — que PASS ne peut pas fermer — reconnecte en
 * silence, sans mot de passe, celui dont on vient précisément de révoquer les
 * jetons : changement de mot de passe, réinitialisation par un conseiller,
 * suppression de compte (VE-11).
 */
export const forceReauthentication = (): void => {
    try {
        sessionStorage.setItem('gravitee_force_login', 'true');
    } catch {
        // sessionStorage indisponible : on éjecte quand même
    }

    useUiStore.getState().setLogin(false);
    clearSession();
};

/**
 * Traite un 401 : rafraîchit puis rejoue `retry`, ou éjecte. Rejette toujours avec `error`
 * quand la session ne peut pas être rétablie.
 */
export const recoverFrom401 = <T>(retry: () => Promise<T>, error: unknown): Promise<T> => {
    if (!AUTH_GRAVITEE_ENABLED) {
        forceReauthentication();
        return Promise.reject(error);
    }

    if (isRefreshing) {
        return new Promise<T>((resolve, reject) => {
            refreshQueue.push((success) => {
                if (!success) {
                    reject(error);
                    return;
                }
                // Rejouer — le nouveau cookie est déjà en place
                retry().then(resolve, () => reject(error));
            });
        });
    }

    isRefreshing = true;

    return refreshGraviteeToken().then((result) => {
        isRefreshing = false;

        if (!result) {
            forceReauthentication();
            processQueue(false);
            throw error;
        }

        processQueue(true);

        return retry().catch(() => {
            forceReauthentication();
            throw error;
        });
    });
};
